import React, { useState, useEffect, useCallback, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
// Críticos / shell — eager (caminho de entrada).
import LoginPage from './pages/LoginPage';
import TrocaSenhaObrigatoriaPage from './pages/TrocaSenhaObrigatoriaPage';
import DashboardLayout from './components/DashboardLayout';
import ErrorBoundary from './components/ErrorBoundary';
import MobileLayout from './components/MobileLayout';

// Páginas sob demanda (code-splitting) — corta o bundle inicial (libs pesadas como
// recharts/leaflet/jspdf/xlsx passam a carregar só na tela que as usa).
const RedefinirSenhaPage = lazy(() => import('./pages/RedefinirSenhaPage'));
const AcompanhamentoPublicoPage = lazy(() => import('./pages/AcompanhamentoPublicoPage'));
const StyleguidePage = lazy(() => import('./pages/StyleguidePage'));
const WeeklyView = lazy(() => import('./pages/WeeklyView'));
const ColaboradoresPage = lazy(() => import('./pages/ColaboradoresPage'));
const VeiculosPage = lazy(() => import('./pages/VeiculosPage'));
const UsersPage = lazy(() => import('./pages/UsersPage'));
const ClientesPage = lazy(() => import('./pages/ClientesPage'));
const OrdensServicoPage = lazy(() => import('./pages/OrdensServicoPage'));
const ConfiguracoesPage = lazy(() => import('./pages/ConfiguracoesPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const RelatoriosPage = lazy(() => import('./pages/RelatoriosPage'));
const RDOPage = lazy(() => import('./pages/RDOPage'));
const RNCPage = lazy(() => import('./pages/RNCPage'));
const RRMPage = lazy(() => import('./pages/RRMPage'));
const ChecklistPage = lazy(() => import('./pages/ChecklistPage'));
const MobileColaboradorPage = lazy(() => import('./pages/MobileColaboradorPage'));
const NotificacoesPage = lazy(() => import('./pages/NotificacoesPage'));
const NotificacoesAdminPage = lazy(() => import('./pages/NotificacoesAdminPage'));
const PerfilColaboradorPage = lazy(() => import('./pages/PerfilColaboradorPage'));
const MapaDemandas = lazy(() => import('./pages/MapaDemandas'));
const MapaColaboradores = lazy(() => import('./pages/MapaColaboradores'));
const DashboardMobilePage = lazy(() => import('./pages/DashboardMobilePage'));
const ObrasPage = lazy(() => import('./pages/ObrasPage'));
const TerceirosPage = lazy(() => import('./pages/TerceirosPage'));
const CronogramasPage = lazy(() => import('./pages/CronogramasPage'));
const DashboardObrasPage = lazy(() => import('./pages/DashboardObrasPage'));
const MateriaisPage = lazy(() => import('./pages/MateriaisPage'));
const EstoquePage = lazy(() => import('./pages/EstoquePage'));
const PropostasPage = lazy(() => import('./pages/PropostasPage'));
const SolicitacoesGeraisPage = lazy(() => import('./pages/SolicitacoesGeraisPage'));
const ChecklistMobilizacaoPage = lazy(() => import('./pages/ChecklistMobilizacaoPage'));
const TemplatesChecklistPage = lazy(() => import('./pages/TemplatesChecklistPage'));
const RelatoriosRNCPage = lazy(() => import('./pages/RelatoriosRNCPage'));
const RelatoriosRRMPage = lazy(() => import('./pages/RelatoriosRRMPage'));
const BackupPage = lazy(() => import('./pages/BackupPage'));
// O COMERCIAL EM QUATRO MENUS (dono, 2026-08-08). As telas são as MESMAS de dentro do antigo
// CRM — o que mudou é que cada uma tem rota própria em vez de ser uma aba. `/crm` continua
// existindo e REDIRECIONA para Negócios: quem tem a URL antiga salva não bate em tela branca.
const LeadsPage = lazy(() => import('./pages/ComercialPages').then((m) => ({ default: m.LeadsPage })));
const NegociosPage = lazy(() => import('./pages/ComercialPages').then((m) => ({ default: m.NegociosPage })));
const AgendaPage = lazy(() => import('./pages/ComercialPages').then((m) => ({ default: m.AgendaPage })));
const ChatPage = lazy(() => import('./pages/ChatPage'));
const PropostasResidencialPage = lazy(() => import('./pages/PropostasResidencialPage'));
const AssistenteIAPage = lazy(() => import('./pages/AssistenteIAPage'));
const EsteiraPage = lazy(() => import('./pages/EsteiraPage'));
const CatalogoDeServicosPage = lazy(() => import('./pages/CatalogoDeServicosPage'));
const BOMPage = lazy(() => import('./pages/BOMPage'));
const TarefasPage = lazy(() => import('./pages/TarefasPage'));
const MinhasTarefasPage = lazy(() => import('./pages/MinhasTarefasPage'));
import './App.css';

// Versão atual do app (atualizar a cada deploy)
const APP_VERSION = '2.3.0';

const App = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  // Verificar se há atualização disponível
  const checkForUpdates = useCallback(async () => {
    try {
      // Usar URL relativa
      const response = await fetch(`/api/version`, { 
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.version && data.version !== APP_VERSION) {
          setUpdateAvailable(true);
        }
      }
    } catch (error) {
      // Silently fail - não é crítico
    }
  }, []);

  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    if (savedUser && token) {
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);

    // Verificar atualizações a cada 5 minutos
    checkForUpdates();
    const interval = setInterval(checkForUpdates, 5 * 60 * 1000);

    // Auto-logout à meia-noite
    const scheduleMidnightLogout = () => {
      const now = new Date();
      const midnight = new Date(now);
      midnight.setHours(24, 0, 0, 0);
      const msUntilMidnight = midnight.getTime() - now.getTime();
      return setTimeout(() => {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        setUser(null);
        window.location.reload();
      }, msUntilMidnight);
    };
    const midnightTimer = scheduleMidnightLogout();
    
    return () => {
      clearInterval(interval);
      clearTimeout(midnightTimer);
    };
  }, [checkForUpdates]);

  // Função para recarregar a página
  const handleUpdate = () => {
    // Limpar cache e recarregar
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }
    window.location.reload(true);
  };

  const handleLogin = (userData, token) => {
    localStorage.setItem('user', JSON.stringify(userData));
    // O token REAL agora vive só no cookie HttpOnly (setado pelo backend em /auth/login),
    // imune a roubo via XSS. Guardamos só um placeholder não-sensível para os guards
    // legados `if (!token)` das ~20 páginas que fazem fetch manual seguirem válidos —
    // o backend ignora esse header placeholder e cai no cookie.
    localStorage.setItem('token', 'cookie');
    setUser(userData);
  };

  const handleLogout = () => {
    // Limpa o cookie HttpOnly de sessão no servidor (best-effort) além do estado local.
    fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => {});
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    setUser(null);
  };

  // Atualiza o usuário logado (ex.: após trocar a foto de perfil) e persiste.
  const handleUserUpdate = (userData) => {
    setUser((prev) => {
      const merged = { ...prev, ...userData };
      localStorage.setItem('user', JSON.stringify(merged));
      return merged;
    });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Carregando...</div>;
  }

  // Troca de senha OBRIGATÓRIA: enquanto a flag estiver ativa, o app inteiro é bloqueado
  // e só a tela de troca é exibida. O backend também bloqueia todas as rotas (403
  // code=must_change_password), então isto é a UX correspondente ao enforcement do servidor.
  if (user && user.must_change_password) {
    return (
      <ErrorBoundary>
        <Toaster position="top-right" />
        <TrocaSenhaObrigatoriaPage user={user} onDone={handleUserUpdate} onLogout={handleLogout} />
      </ErrorBoundary>
    );
  }

  // Determina a rota inicial baseada no role e permissões do usuário
  const getDefaultRoute = () => {
    if (!user) return '/login';
    if (user.role === 'user') return '/minha-programacao';
    
    // Para admins, encontrar a primeira página acessível
    const perms = user.permissoes || {};
    const routeOrder = [
      { path: '/dashboard', module: 'dashboard' },
      { path: '/programacao', module: 'programacao_operacional' },
      { path: '/ordens-servico', module: 'ordens_servico' },
      { path: '/obras', module: 'obras' },
      { path: '/clientes', module: 'clientes' },
      { path: '/colaboradores', module: 'colaboradores' },
      { path: '/materiais', module: 'materiais' },
      { path: '/cronogramas', module: 'cronogramas' },
      { path: '/mapa', module: 'mapa' },
      { path: '/relatorios', module: 'relatorios' },
      { path: '/solicitacoes-gerais', module: 'solicitacoes_pos_venda' },
      { path: '/propostas', module: 'propostas' },
      // Quem só tem CRM caía no `/dashboard` do fallback — uma tela que ele não pode ver.
      // Com o comercial em quatro rotas, Negócios é a porta certa: é o que ele olha todo dia.
      { path: '/negocios', module: 'crm' },
      { path: '/veiculos', module: 'veiculos' },
      { path: '/relatorios-rnc', module: 'rnc' },
      { path: '/relatorios-rrm', module: 'rrm' },
      { path: '/templates-checklist', module: 'templates_checklist' },
      { path: '/usuarios', module: 'usuarios' },
      { path: '/configuracoes', module: 'configuracoes' },
    ];
    
    for (const route of routeOrder) {
      const level = perms[route.module];
      if (level === 'editar' || level === 'visualizar') {
        return route.path;
      }
    }
    // Fallback se nenhuma permissão definida (admin antigo sem permissoes salvas)
    return '/dashboard';
  };

  return (
    <ErrorBoundary>
    <BrowserRouter>
      <Toaster position="top-right" />
      
      {/* Banner de atualização disponível */}
      {updateAvailable && (
        <div className="fixed top-0 left-0 right-0 bg-blue-600 text-white py-2 px-4 flex items-center justify-center gap-4 z-[9999] shadow-lg">
          <span className="text-sm">🔄 Uma nova versão do sistema está disponível!</span>
          <button
            onClick={handleUpdate}
            className="px-4 py-1 bg-white text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-50 transition-colors"
          >
            Atualizar Agora
          </button>
          <button
            onClick={() => setUpdateAvailable(false)}
            className="text-white/80 hover:text-white"
          >
            ✕
          </button>
        </div>
      )}
      
      <Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" /></div>}>
      <Routes>
        <Route path="/login" element={!user ? <LoginPage onLogin={handleLogin} /> : <Navigate to={getDefaultRoute()} />} />
        {/* Pública: redefinição de senha por token (link de e-mail) */}
        <Route path="/redefinir-senha" element={<RedefinirSenhaPage />} />
        {/* Pública: acompanhamento do CLIENTE por token. Fica ANTES do bloco autenticado de
            propósito — ela não pode redirecionar para /login, e o token é a credencial. */}
        <Route path="/acompanhamento/:token" element={<AcompanhamentoPublicoPage />} />
        {/* Catálogo do Design System (QA/referência) */}
        <Route path="/styleguide" element={<StyleguidePage />} />

        {/* Rotas para Admin - Layout com Sidebar (inclui roles antigos para retrocompatibilidade) */}
        {user && user.role !== 'user' && (
          <Route path="/" element={<DashboardLayout user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} />}>
            <Route index element={<Navigate to="/dashboard" />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="notificacoes" element={<NotificacoesAdminPage />} />
            <Route path="programacao" element={<WeeklyView />} />
            <Route path="mapa" element={<MapaDemandas />} />
            {/* Restrito ao master — a própria página barra quem não é (e o backend também). */}
            <Route path="mapa-colaboradores" element={<MapaColaboradores />} />
            <Route path="obras" element={<ObrasPage />} />
            <Route path="terceiros" element={<TerceirosPage />} />
            <Route path="dashboard-obras" element={<DashboardObrasPage />} />
            <Route path="cronogramas" element={<CronogramasPage />} />
            <Route path="materiais" element={<MateriaisPage />} />
            <Route path="estoque" element={<EstoquePage />} />
            <Route path="propostas" element={<PropostasPage />} />
            <Route path="colaboradores" element={<ColaboradoresPage />} />
            <Route path="colaboradores/:colaboradorId/perfil" element={<PerfilColaboradorPage />} />
            <Route path="veiculos" element={<VeiculosPage />} />
            <Route path="usuarios" element={<UsersPage />} />
            <Route path="clientes" element={<ClientesPage />} />
            <Route path="ordens-servico" element={<OrdensServicoPage />} />
            <Route path="rdo/:demandaId" element={<RDOPage />} />
            <Route path="rnc/:demandaId" element={<RNCPage />} />
            <Route path="rrm/:demandaId" element={<RRMPage />} />
            <Route path="checklist/:checklistId" element={<ChecklistPage />} />
            <Route path="minha-programacao" element={<MobileColaboradorPage />} />
            <Route path="configuracoes" element={<ConfiguracoesPage />} />
            <Route path="relatorios" element={<RelatoriosPage />} />
            <Route path="relatorios-rnc" element={<RelatoriosRNCPage />} />
            <Route path="relatorios-rrm" element={<RelatoriosRRMPage />} />
            <Route path="templates-checklist" element={<TemplatesChecklistPage />} />
            <Route path="backup" element={<BackupPage />} />
            <Route path="leads" element={<LeadsPage />} />
            <Route path="negocios" element={<NegociosPage />} />
            <Route path="agenda" element={<AgendaPage />} />
            {/* A URL antiga do CRM continua levando a algum lugar útil. Deixar `/crm` dar tela
                branca puniria quem tem o link salvo por uma reorganização de menu que não é
                problema dele. */}
            <Route path="crm" element={<Navigate to="/negocios" replace />} />
            <Route path="chat" element={<ChatPage />} />
            <Route path="propostas-residencial" element={<PropostasResidencialPage />} />
            <Route path="assistente" element={<AssistenteIAPage />} />
            <Route path="esteira" element={<EsteiraPage />} />
            {/* Curadoria do catálogo de serviços — entra por Configurações. SEM gate de módulo
                no `ROUTE_MODULE_MAP`: LER o catálogo é de qualquer um logado (é vocabulário, não
                dado, e a rota do servidor diz isso), e ESCREVER já é barrado nos dois lados —
                `require_permission('configuracoes','editar')` no backend e `usePermission` na
                própria tela, que abre em somente leitura para quem não pode. */}
            <Route path="catalogo-servicos" element={<CatalogoDeServicosPage />} />
            <Route path="bom" element={<BOMPage />} />
            <Route path="tarefas" element={<TarefasPage />} />
            {/* SEM GATE DE MODULO: a lista e filtrada no servidor pelo proprio usuario. Ver
                MinhasTarefasPage e o cabecalho de routes/tarefas.py. */}
            <Route path="minhas-tarefas" element={<MinhasTarefasPage />} />
            <Route path="dashboard-mobile" element={<DashboardMobilePage />} />
            {/* Balcão único: Pós-Venda foi unificada em Solicitações Gerais. Rota antiga redireciona (deep-links/bookmarks). */}
            <Route path="solicitacoes-pos-venda" element={<Navigate to="/solicitacoes-gerais" replace />} />
            <Route path="solicitacoes-gerais" element={<SolicitacoesGeraisPage />} />
            <Route path="solicitacoes-operacional" element={<SolicitacoesGeraisPage fixedSetor="operacional" pageTitle="Solicitações Operacional" />} />
            <Route path="solicitacoes-novas-instalacoes" element={<SolicitacoesGeraisPage fixedSetor="novas_instalacoes" pageTitle="Solicitações Novas Instalações" />} />
            <Route path="solicitacoes-om" element={<SolicitacoesGeraisPage fixedSetor="om" pageTitle="Solicitações O&M" />} />
            <Route path="checklist-mobilizacao" element={<ChecklistMobilizacaoPage />} />
          </Route>
        )}

        {/* Nota: roles antigos (admin_material, admin_pos_venda) agora usam role=admin com permissoes granulares */}
        
        {/* Rotas para Colaborador - Layout Mobile */}
        {user?.role === 'user' && user && (
          <Route path="/" element={<MobileLayout user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} />}>
            <Route index element={<Navigate to="/minha-programacao" />} />
            <Route path="minha-programacao" element={<MobileColaboradorPage />} />
            <Route path="notificacoes" element={<NotificacoesPage />} />
            <Route path="rdo/:demandaId" element={<RDOPage />} />
            <Route path="rnc/:demandaId" element={<RNCPage />} />
            <Route path="rrm/:demandaId" element={<RRMPage />} />
            <Route path="checklist/:checklistId" element={<ChecklistPage />} />
            <Route path="*" element={<Navigate to="/minha-programacao" />} />
          </Route>
        )}
        
        {/* Rota catch-all para redirecionar */}
        <Route path="*" element={<Navigate to={user ? getDefaultRoute() : '/login'} />} />
      </Routes>
      </Suspense>
    </BrowserRouter>
    </ErrorBoundary>
  );
};

export default App;
